import { FC, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import {
  Stack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import SearchInput from "components/SearchInput";
import { Form, Formik } from "formik";
import { FaFile } from "react-icons/fa";
import ActionsButton from "components/ActionsButton";
import { useDeleteDialog } from "hooks";
import ConfirmDialog from "./ConfirmDialog";
import { IHouseType, IHouseTypeFormPayload } from "interfaces/houseType";
import useHouseType from "hooks/useHouseType";
import TextInput from "./TextInput";
import { houseTypeValidation } from "utils/validationSchema";

const HouseTypeTable: FC = () => {
  const {
    data,
    loading,
    isLoadingAction,
    deleteHouseType,
    onSubmitForm,
    isOpenForm,
    onCloseForm,
    openAddForm,
    openEditForm,
    formValue,
  } = useHouseType();
  const { isOpenDelete, deleteId, onOpendelete, onCloseDelete } =
    useDeleteDialog();
  const [filter, setFilter] = useState("");
  const columnHelper = createColumnHelper<IHouseType>();

  const columns = [
    columnHelper.accessor("nama", {
      cell: (info) => info.getValue(),
      header: "Nama",
    }),
    columnHelper.accessor("luas", {
      cell: (info) => info.getValue(),
      header: "Luas",
    }),
    columnHelper.accessor("id", {
      header: () => null,
      cell: (info) => (
        <ActionsButton
          id={info.getValue()}
          actions={[
            { label: "Edit", onClick: () => openEditForm(info.getValue()) },
            { label: "Hapus", onClick: () => onOpendelete(info.getValue()) },
          ]}
        />
      ),
      meta: {
        sx: {
          textAlign: "right",
        },
      },
      enableGlobalFilter: false,
      enableSorting: false,
    }),
  ];

  return (
    <>
      <Stack direction="row" alignItems="center" my={6}>
        <SearchInput
          onSearch={(search) => {
            setFilter(search);
          }}
        />
        <Button onClick={openAddForm} leftIcon={<FaFile />}>
          Tambah
        </Button>
      </Stack>
      {data && (
        <DataTable
          isLoading={loading}
          columns={columns}
          data={data}
          filter={filter}
          defaultSort={{
            id: "nama",
            desc: false,
          }}
        />
      )}
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpenForm}
        onClose={onCloseForm}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {formValue.id === "new" ? "Tambah" : "Ubah"} Data
          </ModalHeader>
          <ModalCloseButton />
          <Formik<IHouseTypeFormPayload>
            onSubmit={onSubmitForm}
            initialValues={formValue.initialValues}
            enableReinitialize
            validationSchema={houseTypeValidation}
          >
            <Form>
              <ModalBody>
                <Stack spacing={2}>
                  <TextInput name="nama" id="nama" label="Nama" />
                  <TextInput name="luas" id="luas" label="Luas" />
                </Stack>
              </ModalBody>
              <ModalFooter>
                <Button
                  type="reset"
                  colorScheme="red"
                  mr={3}
                  onClick={onCloseForm}
                >
                  Cancel
                </Button>
                <Button
                  isLoading={isLoadingAction}
                  type="submit"
                  colorScheme="blue"
                >
                  Submit
                </Button>
              </ModalFooter>
            </Form>
          </Formik>
        </ModalContent>
      </Modal>
      <ConfirmDialog
        loading={isLoadingAction}
        isOpen={isOpenDelete}
        onClose={onCloseDelete}
        onConfirm={async () => {
          await deleteHouseType(deleteId);
        }}
      />
    </>
  );
};

export default HouseTypeTable;
